import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  MessageCircle,
  Workflow,
  Settings,
  Zap,
  HelpCircle,
  Bell,
  ChevronLeft,
  ChevronRight,
  Building2,
  Shield,
  KeyRound,
  BookOpen
} from "lucide-react";
import { UI_SECTIONS } from "../config/uiVisibility";
import { isPlatformAdmin } from "../services/authService";

export default function Sidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isCollapsed, setIsCollapsed] = useState(false);

  const navigationItems = [
    {
      name: "Dashboard",
      icon: LayoutDashboard,
      path: "/dashboard",
      isActive: (p) => p === "/dashboard"
    },
    {
      name: "Ask Orbit",
      icon: MessageCircle,
      path: "/ask-orbit",
      isActive: (p) => p === "/ask-orbit"
    },
    ...(UI_SECTIONS.sidebar.workflowsNav
      ? [
          {
            name: "Workflows",
            icon: Workflow,
            path: "/workflows",
            isActive: (p) => p === "/workflows"
          }
        ]
      : []),
    {
      name: "Notification Settings",
      icon: Bell,
      path: "/workflows/notifications",
      isActive: (p) => p.startsWith("/workflows/notifications")
    },
    {
      name: "Profile Settings",
      icon: Settings,
      path: "/profile-settings",
      isActive: (p) => p === "/profile-settings"
    },
    {
      name: "Company Settings",
      icon: Building2,
      path: "/company-settings",
      isActive: (p) => p === "/company-settings"
    },
    {
      name: "SFTP Delivery",
      icon: KeyRound,
      path: "/delivery-access",
      isActive: (p) => p === "/delivery-access"
    },
    ...(isPlatformAdmin()
      ? [
          {
            name: "Admin Portal",
            icon: Shield,
            path: "/admin",
            isActive: (p) => p.startsWith("/admin")
          }
        ]
      : [])
  ];

  // eslint-disable-next-line no-unused-vars -- used by the Quick Actions block, commented out below
  const quickActions = [
    {
      name: "Request Integration",
      icon: Zap,
      action: () => {} // TODO: Open request integration modal
    },
    {
      name: "Get Support",
      icon: HelpCircle,
      action: () => {} // TODO: Open support modal
    }
  ];

  const navButtonClass = (active) =>
    `w-full flex items-center ${
      isCollapsed ? "justify-center" : "space-x-3"
    } px-3 py-2 rounded-lg transition-all ${
      active
        ? "bg-blue-600 text-white shadow-sm"
        : "text-gray-700 hover:bg-gray-100"
    }`;

  return (
    <div
      className={`bg-white border-r border-gray-200 flex flex-col h-screen transition-all duration-300 ${
        isCollapsed ? "w-20" : "w-64"
      }`}
    >
      {/* Logo Section */}
      <div
        className={`px-4 py-[10px] border-b flex ${
          isCollapsed ? "justify-center" : "justify-between"
        } border-gray-200`}
      >
        {!isCollapsed && (
          <div className="flex items-center space-x-3">
            <img src="/logo.svg" alt="Orbit Logo" className="w-10 h-10" />
            <div>
              <h1 className="text-sm font-bold text-gray-900">Orbit</h1>
              <span className="text-xs bg-gray-100 px-2 py-0.5 rounded-full">
                Beta
              </span>
            </div>
          </div>
        )}
        {isCollapsed && (
          <img src="/logo.svg" alt="Orbit Logo" className="w-8 h-8" />
        )}
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
        >
          {isCollapsed ? (
            <ChevronRight className="w-4 h-4 text-gray-600" />
          ) : (
            <ChevronLeft className="w-4 h-4 text-gray-600" />
          )}
        </button>
      </div>

      {/* Navigation Items */}
      <nav className="flex-1 p-3 overflow-y-auto">
        {navigationItems.map((item) => {
          const Icon = item.icon;
          const active = item.isActive(location.pathname);
          return (
            <button
              key={item.name}
              type="button"
              onClick={() => navigate(item.path)}
              title={isCollapsed ? item.name : ""}
              className={navButtonClass(active)}
            >
              <Icon className="w-4 h-4 shrink-0" />
              {!isCollapsed && (
                <span className="font-medium text-sm">{item.name}</span>
              )}
            </button>
          );
        })}

        <div className="pt-6 border-b border-gray-300"></div>
        {/* Quick Actions Section */}
        {/* <div className="pt-6">
          {!isCollapsed && (
            <h3 className="px-4 text-xs font-semibold text-gray-600 tracking-wider mb-3">
              Quick Actions
            </h3>
          )}
          {quickActions.map((action) => {
            const Icon = action.icon;
            return (
              <button
                key={action.name}
                type="button"
                onClick={action.action}
                title={isCollapsed ? action.name : ""}
                className={`w-full flex items-center ${
                  isCollapsed ? "justify-center" : "space-x-3"
                } px-4 py-3 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors`}
              >
                <Icon className="w-4 h-4" />
                {!isCollapsed && (
                  <span className="font-medium text-sm">{action.name}</span>
                )}
              </button>
            );
          })}
        </div> */}
      </nav>

      {/* Bottom Section - User Guide, Notifications */}
      <div className="border-t border-gray-200">
        <a
          href="/user-guide"
          target="_blank"
          rel="noopener noreferrer"
          title={
            isCollapsed
              ? "User Guide"
              : "Open the Orbit User Guide in a new tab"
          }
          className={`w-full flex items-center ${
            isCollapsed ? "justify-center" : "space-x-3"
          } px-4 py-4 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors`}
        >
          <BookOpen className="w-4 h-4" />
          {!isCollapsed && (
            <span className="font-medium text-sm">User Guide</span>
          )}
        </a>
        <button
          type="button"
          title={isCollapsed ? "Notifications" : ""}
          className={`w-full flex items-center ${
            isCollapsed ? "justify-center" : "space-x-3"
          } px-4 py-4 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors relative`}
        >
          <Bell className="w-4 h-4" />
          {!isCollapsed && (
            <span className="font-medium text-sm">Notifications</span>
          )}
          <span
            className={`absolute ${
              isCollapsed
                ? "top-2 right-2"
                : "right-4 top-1/2 transform -translate-y-1/2"
            } w-2 h-2 bg-blue-600 rounded-full`}
          ></span>
        </button>
      </div>
    </div>
  );
}
